/* A wrapper around axios to allow authenticated and unauthenticated requests to the API. */
import axios from 'axios';
import { getStore } from './../redux/store-registry';
import { localAuthorization, usesLocalAuth } from './../auth/login/local-auth';
import { isRefusalOfCredential } from './../auth/login/session-ended';
import { sessionEnded } from './../auth/login/redux/actions';
import * as _ from 'lodash';

// Where the backend's API lives. Defaults to /api on the host serving the UI,
// which the development proxy in setupProxy.js relies on. When the backend is
// on another host, set REACT_APP_API_URL at build time, e.g.
// REACT_APP_API_URL=https://api.example.com/api npm run build
const apiUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
const baseUrl = apiUrl + '/admin';

/*
  A request that carried a credential and was answered 401 means the server no longer accepts it (an account that is not active, a
  token it refuses): the session is over, whatever the screen was doing. It ends it, and the login page says why (session-ended.js).
  The error goes on to the caller flagged, so that what it shows is not a vague "Unauthorized" for each request that was in the air
  (see makeErrorPayload). The login's own 401 carries no credential and is left alone.
*/
axios.interceptors.response.use(undefined, error => {
    if (isRefusalOfCredential(error)) {
        error.sessionEnded = true;

        const store = getStore();
        store && store.dispatch(sessionEnded());
    }
    return Promise.reject(error);
});

const queryParamsToUrl = queryParams => {
    const serialisedUrl = new URLSearchParams(
        _.omitBy(queryParams, _.isUndefined)
    ).toString();

    return serialisedUrl ? '?' + serialisedUrl : '';
};

export function getAuthenticated(url, queryParams = {}, headers = {}) {
    return get(url + queryParamsToUrl(queryParams), {
        ...authorizationHeader(),
        ...headers
    });
}

export function postAuthenticated(url, postData) {
    return post(url, postData, authorizationHeader());
}

export function putAuthenticated(url, putData) {
    return put(url, putData, authorizationHeader());
}

export function patchAuthenticated(url, patchData) {
    return patch(url, patchData ? patchData : {}, authorizationHeader());
}

export function deleteAuthenticated(url) {
    return deleteRequest(url, authorizationHeader());
}

export function get(url, headers) {
    return axios.get(fullUrl(url), headers ? headers : {});
}

export function post(url, data, headers) {
    return axios.post(fullUrl(url), data, headers ? headers : {});
}

export function put(url, data, headers) {
    return axios.put(fullUrl(url), data, headers ? headers : {});
}

export function patch(url, data, headers) {
    return axios.patch(fullUrl(url), data, headers ? headers : {});
}

export function deleteRequest(url, headers) {
    return axios.delete(fullUrl(url), headers ? headers : {});
}

function authorizationHeader() {
    // A backend that checks passwords itself issues no token and wants the password with each request.
    if (usesLocalAuth()) {
        return { headers: { Authorization: localAuthorization() } };
    }

    // todo: Check if we can use selectors here.
    const state = getStore().getState();
    const accessToken = state.authentication.token;
    // todo [mandy]: check if null here?
    return {
        headers: {
            Authorization: 'Bearer ' + accessToken
        }
    };
}

function fullUrl(url) {
    return baseUrl + url;
}
